import {
  IFinancingInsight,
  IFinancingProjectionTimelinePoint,
} from "../types/ownerDashboard.types";
import {
  buildFinancingProjectionTimeline,
  FinancingProjectionResult,
  injectFinancingMilestonePoint,
  monthlyRateFromAnnualPct,
  normalizeProjectionTimeline,
} from "./buildFinancingProjectionTimeline";

const MIN_PROJECTION_CHART_POINTS = 2;

function mapTimelinePoints(
  timeline: IFinancingProjectionTimelinePoint[]
): FinancingProjectionResult["timeline"] {
  return timeline.map((point) => ({
    month: point.month_offset,
    projectedRent: point.projected_rent,
    installment: point.monthly_installment,
  }));
}

function shouldUseClientProjection(
  mappedTimeline: FinancingProjectionResult["timeline"],
  effectiveMonthlyRent: number
): boolean {
  if (mappedTimeline.length < MIN_PROJECTION_CHART_POINTS) {
    return true;
  }

  const hasInvalidProjectedRent =
    effectiveMonthlyRent > 0 &&
    mappedTimeline.every((point) => point.projectedRent <= 0);

  return hasInvalidProjectedRent;
}

function buildTimelineFromApi(
  mappedTimeline: FinancingProjectionResult["timeline"],
  insight: IFinancingInsight,
  ipca12mAccumulatedPct: number
): FinancingProjectionResult {
  const milestoneMonth = insight.covers_installment
    ? 0
    : insight.months_until_milestone;

  const monthlyIndexRate = monthlyRateFromAnnualPct(ipca12mAccumulatedPct);
  const normalizedTimeline = normalizeProjectionTimeline(
    mappedTimeline,
    insight.monthly_installment,
    insight.amortization_system === "sac"
  );

  return {
    timeline: injectFinancingMilestonePoint(
      normalizedTimeline,
      milestoneMonth,
      insight.average_monthly_net_rent,
      insight.monthly_installment,
      monthlyIndexRate
    ),
    milestoneMonth,
  };
}

export function mapApiProjectionTimeline(
  timeline: IFinancingProjectionTimelinePoint[] | undefined,
  insight: IFinancingInsight,
  ipca12mAccumulatedPct: number
): FinancingProjectionResult {
  const clientProjection = buildFinancingProjectionTimeline(
    insight.average_monthly_net_rent,
    insight.monthly_installment,
    ipca12mAccumulatedPct,
    insight.covers_installment
  );

  if (!timeline?.length) {
    return clientProjection;
  }

  const mappedTimeline = mapTimelinePoints(timeline);

  if (shouldUseClientProjection(mappedTimeline, insight.average_monthly_net_rent)) {
    return clientProjection;
  }

  return buildTimelineFromApi(mappedTimeline, insight, ipca12mAccumulatedPct);
}
